import { useEffect } from "react";

interface DocumentMetaOptions {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  noindex?: boolean;
}

const DEFAULT_OG_IMAGE = "https://www.saadtak.com/og-image.png";
const SITE_NAME = "سعدتك - Saadtak";

/**
 * Sets dynamic <title>, <meta>, and Open Graph tags for SEO.
 * Cleans up on unmount to restore defaults from index.html.
 */
export function useDocumentMeta({
  title,
  description,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  articlePublishedTime,
  articleModifiedTime,
  noindex = false,
}: DocumentMetaOptions) {
  useEffect(() => {
    // Title
    const prevTitle = document.title;
    document.title = title;

    // Helper to set or create a meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    // Description
    setMeta("name", "description", description);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:type", ogType);
    if (canonicalUrl) {
      setMeta("property", "og:url", canonicalUrl);
    }
    setMeta("property", "og:site_name", SITE_NAME);

    // Twitter Card
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    // Canonical - update the existing element managed by DynamicCanonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const prevCanonical = canonicalEl?.getAttribute("href") || "";
    if (canonicalUrl) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonicalUrl);
    }

    // Article meta (for blog posts)
    const createdEls: HTMLMetaElement[] = [];
    if (articlePublishedTime) {
      createdEls.push(setMeta("property", "article:published_time", articlePublishedTime));
    }
    if (articleModifiedTime) {
      createdEls.push(setMeta("property", "article:modified_time", articleModifiedTime));
    }

    // Noindex
    let robotsEl: HTMLMetaElement | null = null;
    if (noindex) {
      robotsEl = setMeta("name", "robots", "noindex, nofollow");
    }

    // Cleanup: restore defaults
    return () => {
      document.title = prevTitle;
    };
  }, [title, description, canonicalUrl, ogImage, ogType, articlePublishedTime, articleModifiedTime, noindex]);
}
